import {
  taskDetail,
  rewardInfo,
  RewardType,
  RewardStatus,
  TaskStatus,
  ActivityType,
  RewardFormType,
} from '@/types';
import { useFm } from '@web3/base-hooks';
import { Button, Tooltip } from '@bfe/moly';
import { Switch, Case, If, Then } from 'react-if';
import { FollowersIcon, HelpIcon } from '@bfe/moly-icons';
import { TextClamp } from '../TextClamp';
import Link from 'next/link';
import {
  appPath,
  formatKMB,
  isMobile,
  replaceBybitToLocationHost,
} from '@web3/base-utils';
import cls from 'classnames';
import { Page } from '@/config/track-config';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import styles from './index.module.scss';

// 加载 UTC 插件
dayjs.extend(utc);

const iconDefault = `${appPath}/images/activity/icon-default.svg`;
const hotIcon = `${appPath}/images/activity/hot.svg`;

type ITaskReward = {
  activityType: number | undefined;
  status: number;
  verified: boolean;
  data: taskDetail;
  handleDraw: (data: taskDetail, reward: rewardInfo) => void;
  viewReward: () => void;
};

export function TaskReward(props: ITaskReward) {
  const { activityType, status, verified, data } = props;
  const fm = useFm();

  const filterConfigId = [230, 250];

  return (
    <div
      className={cls([
        'flex flex-col bg-card relative',
        isMobile ? 'w-full p-4' : 'w-[457px] p-8 ',
        styles.container,
      ])}
    >
      <div className="flex gap-4">
        <div
          className={cls([
            'overflow-hidden border border-1 border-gray-400',
            isMobile
              ? 'w-12 h-12 rounded-full flex-shrink-0'
              : 'w-24 h-24 rounded-full',
          ])}
        >
          <img
            className={isMobile ? 'w-full' : cls('w-24', 'h-24')}
            src={replaceBybitToLocationHost(data.taskIconUrl)}
            alt=""
          />
        </div>
        <div
          className={cls([
            'flex-1  flex flex-col',
            isMobile ? 'w-full' : 'w-[281px]',
          ])}
        >
          <div className="flex justify-between items-center gap-1">
            <div className="flex-1 flex items-center gap-1 w-[217px]">
              <div
                className={cls([
                  'task-name max-w-[194px] cursor-pointer',
                  isMobile && 'task-name-h5',
                ])}
              >
                <Tooltip title={data.taskName}>
                  <h1>{data.taskName}</h1>
                </Tooltip>
              </div>
              <img src={hotIcon} className="inline w-[18px] h-[18px]" alt="" />
            </div>
            <div className="flex items-center justify-end gap-1 text-xs !md:text-sm">
              <FollowersIcon
                style={{
                  width: isMobile ? 14 : 16,
                  height: isMobile ? 14 : 16,
                }}
              />
              <span>
                {data?.participants === 0
                  ? '--'
                  : formatKMB(data?.participants, 2)}
              </span>
            </div>
          </div>
          <TextClamp
            line={isMobile ? 1 : 2}
            text={data?.taskDetail || ''}
            className="mt-1 !md:mt-4 text-xs !md:text-sm text-gray1 font-normal"
          />
        </div>
      </div>
      {/* Rewards list */}
      <h4 className="mt-6 flex gap-2 text-sm !md:text-base font-medium">
        <span className={cls([isMobile && 'text-gray1'])}>{fm('rewards')}</span>
        {data.rewardList?.length && (
          <Tooltip title={data.rewardList[0].detail} placement="top">
            <HelpIcon
              className={cls([
                'text-gray cursor-pointer relative',
                isMobile ? 'top-[2px] w-4 h-4' : 'top-[3px] hover:opacity-80',
              ])}
            />
          </Tooltip>
        )}
      </h4>
      {data.rewardList?.map((reward, index1) => {
        const nowUTC = dayjs.utc(reward.recordCreatedAt);

        const nextDayUTC = nowUTC.add(1, 'day');

        // 格式化为日期字符串
        const nextDayUTCFormatted = nextDayUTC.format('YYYY.MM.DD');

        return (
          <div className="mt-4 bg-card py-2 !md:py-4 rounded-lg" key={index1}>
            <Switch>
              <Case condition={reward.rewardType === RewardType.blindbox}>
                {reward?.mysteryBoxRewards?.map((boxReward) => (
                  <div
                    className="flex gap-4 items-center mb-2 h-12 font-medium"
                    key={boxReward.name}
                  >
                    <img
                      className="w-9 h-9"
                      src={boxReward.iconUrl || iconDefault}
                      alt=""
                    />
                    <h2>{boxReward.name}</h2>
                  </div>
                ))}
              </Case>
              <Case condition="default">
                <div className="flex items-center gap-4">
                  <img
                    src={reward.iconUrl || iconDefault}
                    alt=""
                    className={cls([
                      'rounded-full',
                      isMobile ? 'w-8 h-8' : ' w-12 h-12',
                    ])}
                  />
                  <div className="flex-1">
                    <h4
                      className={cls(
                        isMobile
                          ? 'text-white text-xs font-medium'
                          : 'text-main text-base font-medium'
                      )}
                    >
                      {reward.name}
                    </h4>
                  </div>
                </div>
              </Case>
            </Switch>
            <If condition={verified}>
              <Then>
                <div className="mt-4 w-full">
                  <Switch>
                    <Case
                      condition={
                        reward.rewardType === RewardType.token ||
                        reward.rewardType === RewardType.blindbox
                      }
                    >
                      <Switch>
                        {/* 未抽奖 */}
                        <Case
                          condition={reward.status === RewardStatus.noClaim}
                        >
                          <Button
                            variant={'primary'}
                            className={cls('w-full', styles.button)}
                            onClick={() => props.handleDraw(data, reward)}
                            data-spm-even="Web3UserClick"
                            data-spm-data={JSON.stringify({
                              page_name: Page.AirdropArcadeDetail,
                              button_name: 'Lottery',
                              Task_index: data.configId,
                              activity_index: data.activityId,
                            })}
                          >
                            {fm('luckyDraw')}
                          </Button>
                        </Case>
                        {/* 已抽奖且中奖，未空投 */}
                        <Case
                          condition={reward.status === RewardStatus.claimed}
                        >
                          <Button
                            variant={'secondary'}
                            className={cls('w-full', styles.button)}
                            disabled={true}
                          >
                            {fm('pendingAirdrop')}
                          </Button>
                        </Case>
                        {/* 已抽奖,未中奖 */}
                        <Case condition={reward.status === RewardStatus.noWin}>
                          <Button
                            variant={'secondary'}
                            className={cls('w-full', styles.button)}
                            disabled={true}
                          >
                            {fm('notWinning')}
                          </Button>
                        </Case>
                        {/* 奖励已过期 */}
                        <Case
                          condition={reward.status === RewardStatus.expired}
                        >
                          <Button
                            variant={'secondary'}
                            className={cls('w-full', styles.button)}
                            disabled={true}
                          >
                            {fm('rewardExpired')}
                          </Button>
                        </Case>
                        {/* 已中奖，奖励已空投 */}
                        <Case
                          condition={reward.status === RewardStatus.airdroped}
                        >
                          <Button
                            variant={'secondary'}
                            className={cls('w-full', styles.button)}
                            onClick={props.viewReward}
                            data-spm-even="Web3UserClick"
                            data-spm-data={JSON.stringify({
                              page_name: Page.AirdropArcadeDetail,
                              button_name: 'CheckRewards',
                              Task_index: data.configId,
                              activity_index: data.activityId,
                            })}
                          >
                            {fm('viewReward')}
                          </Button>
                        </Case>
                        {/* 等待开奖 */}
                        <Case condition={reward.status === RewardStatus.delay}>
                          <Button
                            variant={'secondary'}
                            className={cls('w-full', styles.button)}
                            disabled={true}
                          >
                            {fm('waitingForDraw')}
                          </Button>
                          {reward.status === RewardStatus.delay &&
                            reward.rewardForm === RewardFormType.Delay && (
                              <div className={styles.delayTipArea}>
                                <div className={styles.symbol}>*</div>
                                <div className={styles.delayTip}>
                                  {fm('drawingToday', {
                                    num: nextDayUTCFormatted,
                                  })}
                                </div>
                              </div>
                            )}
                        </Case>
                      </Switch>
                    </Case>
                    <Case
                      condition={
                        reward.rewardType !== RewardType.multiple &&
                        status === TaskStatus.comleted &&
                        !data.displayOnly &&
                        !filterConfigId.includes(data.configId)
                      }
                    >
                      <Button
                        variant={'secondary'}
                        className={cls('text-white', 'w-full', styles.button)}
                        onClick={props.viewReward}
                        data-spm-even="Web3UserClick"
                        data-spm-data={JSON.stringify({
                          page_name: Page.AirdropArcadeDetail,
                          button_name: 'CheckRewards',
                          Task_index: data.configId,
                          activity_index: data.activityId,
                        })}
                      >
                        {fm('viewReward')}
                      </Button>
                    </Case>
                    <Case
                      condition={
                        reward.rewardType === RewardType.multiple &&
                        reward.webGoUrl
                      }
                    >
                      <Link href={reward.webGoUrl} target="_blank">
                        <Button
                          variant={'secondary'}
                          className={cls('text-white', 'w-full', styles.button)}
                          data-spm-even="Web3UserClick"
                          data-spm-data={JSON.stringify({
                            page_name: Page.AirdropArcadeDetail,
                            button_name: 'CheckDappRewards',
                            Task_index: data.configId,
                            activity_index: data.activityId,
                          })}
                        >
                          {fm('checkRewardsOnDapp')}
                        </Button>
                      </Link>
                    </Case>
                    <Case condition={status === TaskStatus.claimed}>
                      <Button
                        variant={'secondary'}
                        className={cls('text-white', 'w-full', styles.button)}
                        disabled={true}
                      >
                        {fm('rewardExpired')}
                      </Button>
                    </Case>
                  </Switch>
                </div>
              </Then>
            </If>
          </div>
        );
      })}
      {status <= TaskStatus.unComlete &&
        activityType !== ActivityType.noStatus &&
        !data.displayOnly &&
        !filterConfigId.includes(data.configId) &&
        !isMobile && (
          <Button
            variant={'secondary'}
            className={cls('text-white', 'mt-4', styles.button)}
            disabled={true}
          >
            {fm('taskIncomplete')}
          </Button>
        )}
    </div>
  );
}
